import { useEffect, useState } from "react";
import axios from "axios";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const setApi = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/sets`;
const gamesApi = `${process.env.REACT_APP_BACKEND_URL}/api/catalog/public/games`;

const conditions = [
  "Near Mint",
  "Lightly Played",
  "Moderately Played",
  "Heavily Played",
  "Damaged",
  "Sealed",
  "Ungraded",
  "Other",
];

const blankRecord = {
  game: "Pokémon",
  card_name: "",
  set_name: "",
  set_code: "",
  catalog_set_id: "",
  variant: "",
  language: "English",
  quantity: 1,
  condition: "Near Mint",
  unit_cost: "0",
  market_value: "0",
  asking_price: "0",
  inventory_status: "Available",
  listing_status: "Not Listed",
  storage: { custom_label: "" },
  low_stock_threshold: 0,
  notes: "",
  tags: [],
};

export default function InventoryRecordDialog({
  item,
  onOpenChange,
  onSave,
  open,
}) {
  const [form, setForm] = useState(blankRecord);
  const [sets, setSets] = useState([]);
  const [games, setGames] = useState(["Pokémon"]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(
      item
        ? { ...blankRecord, ...item, storage: item.storage || {} }
        : blankRecord,
    );
    setDirty(false);
  }, [item, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    axios
      .get(setApi, {
        params: { game: form.game, search: form.set_name, limit: 30 },
        withCredentials: true,
      })
      .then((response) => setSets(response.data.sets))
      .catch(() => setSets([]));
  }, [form.game, form.set_name, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    axios
      .get(gamesApi, { withCredentials: true })
      .then((response) => {
        setGames(response.data.games);
      })
      .catch(() => setGames(["Pokémon"]));
  }, [open]);

  const update = (field, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateStorage = (value) => {
    setDirty(true);
    setForm((current) => ({
      ...current,
      storage: { ...current.storage, custom_label: value },
    }));
  };

  const chooseSet = (value) => {
    const selected = sets.find((set) => set.set_name === value);
    update("set_name", value);
    if (selected) {
      update("set_code", selected.set_code || "");
      update("catalog_set_id", selected.catalog_set_id || "");
    }
  };

  const close = (nextOpen) => {
    if (
      !nextOpen &&
      dirty &&
      !window.confirm("Discard unsaved inventory changes?")
    ) {
      return;
    }
    onOpenChange(nextOpen);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...form,
        quantity: Number(form.quantity),
        low_stock_threshold: Number(form.low_stock_threshold),
      });
      setDirty(false);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="workspace-dialog workspace-dialog-wide"
        data-testid="inventory-record-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {item ? "Edit inventory record" : "Add inventory record"}
          </DialogTitle>
          <DialogDescription>
            Market value is manually entered and is not a live price feed.
          </DialogDescription>
        </DialogHeader>
        <form
          className="workspace-form workspace-form-grid"
          onSubmit={submit}
          data-testid="inventory-record-form"
        >
          <label>
            <span>Game</span>
            <input
              list="inventory-game-options"
              value={form.game}
              onChange={(event) => {
                update("game", event.target.value);
                update("set_name", "");
                update("set_code", "");
                update("catalog_set_id", "");
              }}
              data-testid="inventory-game-input"
            />
            <datalist id="inventory-game-options">
              {games.map((game) => (
                <option key={game} value={game} />
              ))}
            </datalist>
          </label>
          <label>
            <span>Card name</span>
            <input
              value={form.card_name}
              onChange={(event) => update("card_name", event.target.value)}
              required
              data-testid="inventory-card-name-input"
            />
          </label>
          <label>
            <span>Set selection</span>
            <input
              list="inventory-set-options"
              value={form.set_name || ""}
              onChange={(event) => chooseSet(event.target.value)}
              placeholder="Search frozen library"
              data-testid="inventory-set-combobox"
            />
            <datalist id="inventory-set-options">
              {sets.map((set) => (
                <option
                  key={set.set_id}
                  value={set.set_name}
                  label={`${set.display_name || set.set_name} (${set.set_code || "No code"})`}
                />
              ))}
            </datalist>
          </label>
          <label>
            <span>Set code</span>
            <input
              value={form.set_code || ""}
              onChange={(event) => update("set_code", event.target.value)}
              data-testid="inventory-set-code-input"
            />
          </label>
          <label>
            <span>Quantity</span>
            <input
              min="1"
              type="number"
              value={form.quantity}
              onChange={(event) => update("quantity", event.target.value)}
              required
              data-testid="inventory-quantity-input"
            />
          </label>
          <label>
            <span>Condition</span>
            <select
              value={form.condition}
              onChange={(event) => update("condition", event.target.value)}
              data-testid="inventory-condition-input"
            >
              {conditions.map((condition) => (
                <option key={condition}>{condition}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Purchase price per card</span>
            <input
              min="0"
              step="0.01"
              type="number"
              value={form.unit_cost}
              onChange={(event) => update("unit_cost", event.target.value)}
              data-testid="inventory-unit-cost-input"
            />
          </label>
          <label>
            <span>Current market value</span>
            <input
              min="0"
              step="0.01"
              type="number"
              value={form.market_value}
              onChange={(event) => update("market_value", event.target.value)}
              data-testid="inventory-market-value-input"
            />
          </label>
          <label>
            <span>Storage location</span>
            <input
              value={form.storage?.custom_label || ""}
              onChange={(event) => updateStorage(event.target.value)}
              placeholder="Room / cabinet / box / row"
              data-testid="inventory-storage-input"
            />
          </label>
          <label>
            <span>Variant</span>
            <input
              value={form.variant || ""}
              onChange={(event) => update("variant", event.target.value)}
              data-testid="inventory-variant-input"
            />
          </label>
          <label>
            <span>Language</span>
            <input
              value={form.language}
              onChange={(event) => update("language", event.target.value)}
              data-testid="inventory-language-input"
            />
          </label>
          <label>
            <span>Asking price</span>
            <input
              min="0"
              step="0.01"
              type="number"
              value={form.asking_price}
              onChange={(event) => update("asking_price", event.target.value)}
              data-testid="inventory-asking-price-input"
            />
          </label>
          <label className="workspace-form-wide">
            <span>Notes</span>
            <textarea
              value={form.notes || ""}
              onChange={(event) => update("notes", event.target.value)}
              data-testid="inventory-notes-input"
            />
          </label>
          <button
            className="admin-primary-button workspace-form-wide"
            disabled={saving}
            type="submit"
            data-testid="save-inventory-button"
          >
            {saving ? "Saving…" : item ? "Save changes" : "Save inventory"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
